import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiEdit, FiTrash2, FiPlus, FiX } from "react-icons/fi";

const Designation = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    department: "",
  });

  // Fetch designations when component mounts
  useEffect(() => {
    fetchDesignations();
    fetchDepartments();
  }, []);

  const fetchDesignations = async () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");
    const companyId = localStorage.getItem("selectedCompanyId");
    
    if (!companyId) {
      setError("No company selected. Please select a company first.");
      setLoading(false);
      return;
    }

    try {
      // Try with query parameter first
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/designations`,
        { 
          params: { companyId },
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      // If no data, try the previous format as fallback
      if (!response.data || response.data.length === 0) {
        const fallbackResponse = await axios.get(
          `https://customersupport-jhy9.onrender.com/api/designations/company/${companyId}`,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            } 
          }
        );
        setDesignations(fallbackResponse.data || []);
      } else {
        setDesignations(response.data);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching designations:", err);
      setError("Failed to fetch designations. Please check your connection and try again.");
      setDesignations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    const token = localStorage.getItem("authToken");
    const companyId = localStorage.getItem("selectedCompanyId");
    
    if (!companyId) {
      setError("No company selected. Please select a company first.");
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      setDepartments(response.data);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setError("Failed to fetch departments. Some features may be limited.");
      setDepartments([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");
    
    try {
      if (isEditing) {
        await axios.put(
          `https://customersupport-jhy9.onrender.com/api/designations/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          "https://customersupport-jhy9.onrender.com/api/designations/",
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      resetForm();
      fetchDesignations();
      setShowForm(false);
    } catch (err) {
      console.error("Error saving designation:", err);
      setError(err.response?.data?.message || "Error saving designation");
    }
  };

  const handleEdit = (designation) => {
    setFormData({
      name: designation.name,
      department: designation.department._id,
    });
    setEditingId(designation._id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this designation?")) {
      const token = localStorage.getItem("authToken");
      try {
        await axios.delete(
          `https://customersupport-jhy9.onrender.com/api/designations/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchDesignations();
      } catch (err) {
        console.error("Error deleting designation:", err);
        setError("Failed to delete designation");
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: "", department: "" });
    setEditingId(null);
    setIsEditing(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Designation Management</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Add Designation
        </button>
      </div>

      {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">{error}</div>}

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {isEditing ? "Edit Designation" : "Add New Designation"}
            </h2>
            <button onClick={() => {
              setShowForm(false);
              resetForm();
            }} className="text-gray-500 hover:text-gray-700">
              <FiX size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Designation Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {isEditing ? "Update" : "Save"} Designation
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">Loading designations...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {designations.length > 0 ? (
                  designations.map((designation) => (
                    <tr key={designation._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{designation.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {designation.department?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(designation)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <FiEdit className="inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(designation._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 className="inline" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                      No designations found. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Designation;